// Created: 2026-07-20
// Updated: 2026-08-14
import type {
  PatternDetailsResponse,
  PatternSearchResult,
  PatternVersionResponse,
  PaginatedAuthorPatternsResponse,
  PaginatedNewestPatternsFullResponse,
  SearchPatternsFilteredResponse,
  SearchResponse,
  SearchProjectsResponse,
  MaterialPagePatternsResponse,
  MaterialPagePatternUsage,
  ProjectSearchDocument,
} from '@mrb91002/shared-types'

import { API_BASE } from '../config'
import type { ListingBucket } from '../listingBuckets'
import { secureFetch, SECURE_API_BASE } from '../secure/helpers'

import { PublicApiError } from './errors'

/**
 * Newest public patterns, DynamoDB-paginated (lambda-main `GET /api/patterns/public/full`).
 *
 * `lastKey` is the OPAQUE DynamoDB cursor. The query layer carries it in `pageParam`, never in the
 * query key — an infinite query holds all its pages under ONE cache entry (react-query-browse-search
 * § D1). `opts.signal` and the status-bearing `PublicApiError` are additive: the URL, the `limit` and
 * `lastKey` parameters, and the success parsing are unchanged, so a caller that omits `opts` makes a
 * byte-identical request (Req 6.1, 6.2, 6.3).
 */
export async function fetchNewestPatternsFull(
  limit = 5,
  lastKey: Record<string, unknown> | string | null = null,
  opts?: { signal?: AbortSignal },
): Promise<PaginatedNewestPatternsFullResponse> {
  let url = `${API_BASE}/patterns/public/full?limit=${limit}`
  if (lastKey) {
    const lastKeyStr = typeof lastKey === 'string' ? lastKey : JSON.stringify(lastKey)
    url += `&lastKey=${encodeURIComponent(lastKeyStr)}`
  }
  const resp = await fetch(url, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch newest patterns (full)', resp.status)
  return resp.json() as Promise<PaginatedNewestPatternsFullResponse>
}

/**
 * Sorted/date-filtered public patterns, OpenSearch offset-paginated
 * (lambda-main `GET /api/patterns/search-filtered`).
 *
 * `sortBy` and `dateRange` are resource IDENTITY — the backend serves a different ordering AND a
 * different date window, i.e. a different result set, not a client-side reordering of one — so the
 * query layer keys on both. `from` is the offset and rides in `pageParam`. `opts.signal` and the
 * status-bearing error are additive, as above.
 */
export async function searchPatternsWithFilters(
  sortBy: 'newest' | 'highest-rated' | 'most-reviewed',
  dateRange: 'day' | 'week' | 'month' | 'year' | 'all',
  limit = 50,
  from = 0,
  opts?: { signal?: AbortSignal },
): Promise<SearchPatternsFilteredResponse> {
  const url = `${API_BASE}/patterns/search-filtered?sortBy=${sortBy}&dateRange=${dateRange}&limit=${limit}&from=${from}`
  const resp = await fetch(url, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to search patterns', resp.status)
  return resp.json() as Promise<SearchPatternsFilteredResponse>
}

/**
 * Public pattern detail payload (lambda-main `GET /api/patterns/{id}/details`).
 *
 * ANONYMOUS by construction: no `includeUserReview` parameter and NO `Authorization` header
 * (auth-contract-correctness, Req 8 — which supersedes the Wire_Fidelity_Clause that slice 2's
 * Req 2.5 kept alive). The handler resolves no caller, so both were sending information the server
 * could not act on; the header in particular put an `idToken` on an unauthenticated proxy on every
 * pattern page view for no purpose. The caller's own review comes from the SECURE route via
 * `useMyPatternReviewQuery`. The request is now a pure function of `patternId`.
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`
 * (react-query-pattern-detail, Req 2.4). On failure this throws a status-bearing `PublicApiError` so
 * the shared retry predicate can read the HTTP status and never retry a 4xx (Req 10.7, 10.8).
 */
export async function fetchPatternDetails(
  patternId: string,
  opts?: { signal?: AbortSignal },
): Promise<PatternDetailsResponse> {
  const url = `${API_BASE}/patterns/${patternId}/details`
  const resp = await fetch(url, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch pattern details', resp.status)
  return resp.json() as Promise<PatternDetailsResponse>
}

/**
 * One archived version of a pattern (lambda-main `GET /api/pattern/{id}/version/{n}`).
 *
 * A version is a DIFFERENT resource from the current detail payload, which is why the query layer
 * keys it separately (design § D1). `opts.signal` and the status-bearing error are additive, as above.
 */
export async function fetchPatternVersion(
  patternId: string,
  versionNumber: number,
  opts?: { signal?: AbortSignal },
): Promise<PatternVersionResponse> {
  const url = `${API_BASE}/pattern/${encodeURIComponent(patternId)}/version/${versionNumber}`
  const resp = await fetch(url, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch pattern version', resp.status)
  return resp.json() as Promise<PatternVersionResponse>
}

/**
 * PUBLIC continuation page of one author's patterns (lambda-main `GET /api/author/{sn}/patterns`).
 *
 * Identity-INVARIANT: no header is sent and the handler reads no authorizer — its query is
 * `begins_with(GSI3SK, 'public#')`, i.e. the visitor view only. That is why the query layer keys it
 * `['authorPatterns', screenName]` with no identity component (react-query-profile-collections § D3).
 *
 * PRE-EXISTING DEFECT, documented and NOT fixed here: `get-patterns-by-author.ts` ends with
 * `buildPaginatedResponse(patterns, undefined)` — it never forwards `queryRes.LastEvaluatedKey` — so
 * `lastKey` is ALWAYS `null` and a third page is unreachable. The cursor plumbing below is correct and
 * stays; when the handler is fixed the client needs no change.
 *
 * `opts.signal` and the status-bearing `PublicApiError` are additive: the URL, the `lastKey` parameter,
 * and the success parse are unchanged, so a caller that omits `opts` makes a byte-identical request.
 */
export async function fetchAuthorPatterns(
  authorId: string,
  lastKey: string | null = null,
  opts?: { signal?: AbortSignal },
): Promise<PaginatedAuthorPatternsResponse> {
  let url = `${API_BASE}/author/${encodeURIComponent(authorId)}/patterns`
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`
  console.log('[PATTERNS] fetchAuthorPatterns:', { authorId, lastKey, url })
  const resp = await fetch(url, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  console.log('[PATTERNS] fetchAuthorPatterns response:', { status: resp.status, ok: resp.ok })
  if (!resp.ok) throw new PublicApiError('Failed to fetch author patterns', resp.status)
  const data = await resp.json() as PaginatedAuthorPatternsResponse
  console.log('[PATTERNS] fetchAuthorPatterns data:', { itemCount: data.items?.length })
  return data
}

/**
 * How many patterns one bucket page asks for.
 *
 * 24, matching the route's own default, and three times the 8 the anonymous author listing hardcodes:
 * the owner's four segments are the owner's whole view of each bucket, not a teaser strip. The route
 * caps a caller-supplied `limit` at 50.
 */
export const PATTERN_BUCKET_PAGE_SIZE = 24

/**
 * PERSONAL continuation page of ONE Listing_Bucket of the CALLER'S OWN patterns
 * (lambda-secure `GET /api/secure/patterns?bucket=&lastKey=&limit=`; content-visibility-model task 49,
 * Requirements 5.3, 5.4, 10.1-10.3, 12.1, 12.3; BL-330).
 *
 * Identity-VARYING, which is why the query layer keys it under the `['me', …]` namespace and gates it on
 * a token: the route derives its partition from the VERIFIED authorizer claims, so what it answers is
 * the caller's own `pattern_visibility#{id}` range and nobody else's. The screen name is not in the
 * request at all — there is nothing for a caller to name.
 *
 * ## What this replaced, and why it is one function rather than two
 *
 * Before task 49 the owner's four segments were served three different ways: `public` from the profile
 * envelope, `unlisted` and `private` by re-requesting that WHOLE envelope with `?bucket=` (the profile
 * record, four social queries, the collections preview and the entire favorites walk, to fill one tab),
 * and `archived` from `GET /api/secure/pattern/archived`. None of the three non-public segments could
 * page: the only continuation endpoint was the anonymous {@link fetchAuthorPatterns}, whose key
 * condition is the PUBLIC bucket, so replaying a non-public cursor there would have appended public
 * patterns underneath the Unlisted heading. Task 29 suppressed the button rather than ship that.
 *
 * This route serves all four buckets with a real cursor, so the suppression is gone — removed, not
 * disabled. `bucketHasContinuation`, the flag that encoded it, no longer exists.
 *
 * ## The cursor
 *
 * `lastKey` is the opaque DynamoDB cursor, carried in the query layer's `pageParam` and never in the
 * key. The route REFUSES with 400 a cursor whose position names another bucket or another owner's
 * partition, so a cursor cannot silently continue the wrong listing — which is the defect the
 * suppression was protecting against, now prevented at the boundary.
 *
 * The profile envelope's `patterns_lastKey` is a valid seed for the PUBLIC bucket, and that is not a
 * coincidence: both are the `LastEvaluatedKey` of the same key condition over the same partition and
 * the same key range, so the position means the same thing to both. That is what lets the owner's public
 * segment keep page one from the envelope it already fetched and continue from here.
 *
 * `opts.signal` is forwarded so an abandoned navigation is a CANCELLATION rather than a query error.
 */
export async function fetchOwnerPatternBucket(
  bucket: ListingBucket,
  lastKey: string | null,
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<PaginatedAuthorPatternsResponse> {
  const params = new URLSearchParams({ bucket, limit: String(PATTERN_BUCKET_PAGE_SIZE) })
  if (lastKey) params.set('lastKey', lastKey)
  const url = `${SECURE_API_BASE}/patterns?${params.toString()}`
  const data = await secureFetch<PaginatedAuthorPatternsResponse>(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  console.log('[PATTERNS] fetchOwnerPatternBucket:', { bucket, hasCursor: !!lastKey, itemCount: data.items?.length })
  return data
}

export async function searchPatternsSimple(
  query: string,
  limit: number = 10,
  page: number = 1,
): Promise<PatternSearchResult[]> {
  const resp = await fetch(`${API_BASE}/patterns/search?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`)
  if (!resp.ok) throw new Error('Failed to search patterns')
  const data: SearchResponse = await resp.json() as SearchResponse
  return data.items.flatMap((r) => (r.pattern ? [r.pattern] : []))
}

/**
 * Craft projects made from a pattern (lambda-main `GET /api/projects/search?pattern_id=…`).
 *
 * `limit` selects a different list SIZE, so the query layer keys on it — the pattern page's 4-item
 * teaser and 50-item expansion are two separate cache entries (Req 3.5). `opts.signal` and the
 * status-bearing error are additive, as above.
 */
export async function fetchPatternCraftProjects(
  patternId: string,
  limit: number = 6,
  opts?: { signal?: AbortSignal },
): Promise<(ProjectSearchDocument & { _score: number })[]> {
  const resp = await fetch(`${API_BASE}/projects/search?pattern_id=${encodeURIComponent(patternId)}&limit=${limit}`, {
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch pattern craft projects', resp.status)
  const data: SearchProjectsResponse = await resp.json() as SearchProjectsResponse
  return data.items || []
}

export async function fetchPatternsUsingMaterial(
  materialId: string,
  limit: number = 6,
): Promise<MaterialPagePatternUsage[]> {
  const resp = await fetch(`${API_BASE}/materials/${encodeURIComponent(materialId)}/patterns?limit=${limit}`)
  if (!resp.ok) throw new Error('Failed to fetch patterns using material')
  const data: MaterialPagePatternsResponse = await resp.json() as MaterialPagePatternsResponse
  return data.patterns || []
}
